/**
 * Trip-share invite helpers — mirrors iOS SharedTripSyncHelpers matching / labels.
 */

/** Owner local trip id for share APIs (lowercase UUID string). */
export function normalizedTripIdString(tripId: string): string {
  return String(tripId ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Whether a connection-invite `tripId` refers to this local trip.
 * Matches exact id, `{sub}#trip#{id}` suffix, or UUID string equality.
 */
export function connectionInviteMatchesTrip(
  inviteTripId: string | null | undefined,
  tripId: string
): boolean {
  const raw = String(inviteTripId ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return false;
  const target = normalizedTripIdString(tripId);
  if (!target) return false;
  if (raw === target) return true;
  if (raw.endsWith(`#trip#${target}`)) return true;
  // UUID equality (already lowercased; same as normalized)
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(raw)) {
    return raw === target;
  }
  return false;
}

/** Truncated Cognito sub for UI when no display name is available. */
export function shortSubLabel(sub: string): string {
  const trimmed = String(sub ?? '').trim();
  if (trimmed.length <= 8) return trimmed;
  return `${trimmed.slice(0, 8)}…`;
}

/**
 * Primary label for a pending connection invite row.
 * Email stays as-is; `phone_{digits}@…` or `phone_{digits}` shows digits only.
 */
export function connectionInvitePrimaryLabel(inviteeUserId: string): string {
  const raw = String(inviteeUserId ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('phone_')) {
    const after = raw.slice('phone_'.length);
    const at = after.indexOf('@');
    return at >= 0 ? after.slice(0, at) : after;
  }
  return raw;
}
